// ═══════════════════════════════════════════════════════════════════════════
//  Observabilidad.jsx — Fase 4: quién presentó cada examen y cómo le fue
//
//  Vive dentro de ExamsCmsTab (vista «Observabilidad»). Solo lee: el panorama
//  por examen, los intentos recientes, los intentos de UN examen y el detalle
//  de un intento. Los datos ya se guardaban en assessment_sessions.
// ═══════════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';

function cab() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}
const API = '/api/admin/assessment';

function cuando(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
function duracion(seg) {
  if (seg == null) return '—';
  const m = Math.floor(seg / 60), s = seg % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}
function Aprob({ v }) {
  if (v === true) return <span className="ecms-badge ecms-badge-on">Aprobado</span>;
  if (v === false) return <span className="ecms-badge ecms-badge-off">Reprobado</span>;
  return <span className="ecms-dim">—</span>;
}

export default function Observabilidad({ onAviso }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [examen, setExamen] = useState(null);    // { exam, intentos } de un examen
  const [detalle, setDetalle] = useState(null);  // el intento abierto

  useEffect(() => {
    fetch(`${API}/observabilidad`, { headers: cab() })
      .then((r) => r.json()).then((d) => { setData(d); setCargando(false); })
      .catch(() => { onAviso?.({ tipo: 'error', txt: 'No se pudo cargar la observabilidad.' }); setCargando(false); });
  }, [onAviso]);

  async function verExamen(exam_id) {
    try {
      const r = await fetch(`${API}/exams/${exam_id}/attempts`, { headers: cab() });
      setExamen(await r.json());
    } catch { onAviso?.({ tipo: 'error', txt: 'Fallo de conexión.' }); }
  }
  async function verDetalle(session_id) {
    try {
      const r = await fetch(`${API}/attempts/${session_id}`, { headers: cab() });
      setDetalle(await r.json());
    } catch { onAviso?.({ tipo: 'error', txt: 'Fallo de conexión.' }); }
  }

  if (cargando) return <div className="ecms-vacio">Cargando…</div>;
  if (!data) return <div className="ecms-vacio">Sin datos.</div>;

  return (
    <div className="ecms-obs">
      <div className="ecms-salud-cifras">
        <span><b>{data.total_intentos}</b> intentos en total</span>
        <span><b>{data.por_examen.filter((e) => e.intentos > 0).length}</b> exámenes con actividad</span>
      </div>

      {/* Panorama por examen */}
      <div className="ecms-tabla-wrap">
        <table className="ecms-tabla">
          <thead>
            <tr><th>Examen</th><th>Nivel</th><th>Intentos</th><th>Completados</th><th>Promedio</th><th>Aprob./Repr.</th><th></th></tr>
          </thead>
          <tbody>
            {data.por_examen.map((e) => (
              <tr key={e.exam_id}>
                <td className="ecms-code">{e.code}<div className="ecms-dim">{e.title}</div></td>
                <td><span className="ecms-nivel">{e.cefr_target}</span></td>
                <td>{e.intentos}</td>
                <td>{e.completados}</td>
                <td>{e.promedio != null ? `${e.promedio}%` : '—'}</td>
                <td>{e.aprobados != null ? `${e.aprobados} / ${e.reprobados}` : '—'}</td>
                <td className="ecms-fila-acc">
                  {e.intentos > 0 && <button className="ecms-mini" onClick={() => verExamen(e.exam_id)}>Ver intentos</button>}
                </td>
              </tr>
            ))}
            {data.por_examen.length === 0 && (
              <tr><td colSpan={7} className="ecms-vacio">Aún no hay exámenes.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Intentos recientes */}
      <h3 className="ecms-obs-tit">Últimos intentos</h3>
      {data.recientes.length === 0 ? (
        <div className="ecms-vacio">Nadie ha presentado un examen todavía.</div>
      ) : (
        <div className="ecms-tabla-wrap">
          <table className="ecms-tabla">
            <thead>
              <tr><th>Alumno</th><th>Examen</th><th>Nota</th><th></th><th>Cuándo</th><th>Duración</th><th></th></tr>
            </thead>
            <tbody>
              {data.recientes.map((i) => (
                <tr key={i.session_id}>
                  <td>{i.alumno}</td>
                  <td className="ecms-dim">{i.exam_title} <span className="ecms-nivel">{i.cefr_target}</span></td>
                  <td>{i.overall_score != null ? `${i.overall_score}%` : (i.status === 'in_progress' ? 'en curso' : '—')}</td>
                  <td><Aprob v={i.aprobado} /></td>
                  <td className="ecms-dim">{cuando(i.finished_at || i.started_at)}</td>
                  <td className="ecms-dim">{duracion(i.total_time_seconds)}</td>
                  <td className="ecms-fila-acc"><button className="ecms-mini" onClick={() => verDetalle(i.session_id)}>Ver</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {examen && <ModalExamen examen={examen} onDetalle={verDetalle} onCerrar={() => setExamen(null)} />}
      {detalle && <ModalDetalle detalle={detalle} onCerrar={() => setDetalle(null)} />}
    </div>
  );
}

function ModalExamen({ examen, onDetalle, onCerrar }) {
  const ex = examen.exam || {};
  return (
    <div className="ecms-modal" onClick={onCerrar}>
      <div className="ecms-editor" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
        <h3>Intentos de {ex.code} · {ex.title}</h3>
        <p className="ecms-sub">{examen.total} intento(s)</p>
        <div className="ecms-tabla-wrap">
          <table className="ecms-tabla">
            <thead><tr><th>Alumno</th><th>Nota</th><th></th><th>Estado</th><th>Cuándo</th><th>Duración</th><th></th></tr></thead>
            <tbody>
              {examen.intentos.map((i) => (
                <tr key={i.session_id}>
                  <td>{i.alumno}<div className="ecms-dim">{i.email}</div></td>
                  <td>{i.overall_score != null ? `${i.overall_score}%` : '—'}</td>
                  <td><Aprob v={i.aprobado} /></td>
                  <td className="ecms-dim">{i.status}</td>
                  <td className="ecms-dim">{cuando(i.finished_at || i.started_at)}</td>
                  <td className="ecms-dim">{duracion(i.total_time_seconds)}</td>
                  <td className="ecms-fila-acc"><button className="ecms-mini" onClick={() => onDetalle(i.session_id)}>Ver</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ecms-editor-botones"><button className="ecms-btn ecms-btn-primario" onClick={onCerrar}>Cerrar</button></div>
      </div>
    </div>
  );
}

function ModalDetalle({ detalle, onCerrar }) {
  const d = detalle;
  return (
    <div className="ecms-modal" onClick={onCerrar}>
      <div className="ecms-editor" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h3>{d.alumno}</h3>
        <p className="ecms-sub">{d.exam_title} · nivel {d.cefr_target}</p>
        <div className="ecms-salud-cifras">
          <span><b>{d.overall_score != null ? `${d.overall_score}%` : '—'}</b> nota</span>
          <span><Aprob v={d.aprobado} /></span>
          <span><b>{d.n_respuestas}</b> respuestas</span>
          <span><b>{duracion(d.total_time_seconds)}</b></span>
        </div>
        {(d.por_destreza || []).length > 0 && (
          <div className="ecms-secs" style={{ marginTop: 12 }}>
            <div className="ecms-opts-tit">Desglose por destreza</div>
            {d.por_destreza.map((s, i) => (
              <div key={i} className="ecms-sec" style={{ justifyContent: 'space-between' }}>
                <span>{s.label || s.skill}</span>
                <span className="ecms-code">{s.score != null ? `${Math.round(s.score)}%` : (s.pendientes_ia ? 'pendiente IA' : '—')}</span>
              </div>
            ))}
          </div>
        )}
        <div className="ecms-editor-botones"><button className="ecms-btn ecms-btn-primario" onClick={onCerrar}>Cerrar</button></div>
      </div>
    </div>
  );
}
